import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Database, Plus, Save, Trash2, Pencil } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { TextInput } from "../../../ui/components/Form";
import { ListView } from "../../../ui/components/ListView";
import type { ListViewContextAction } from "../../../ui/components/ListView";
import { ListViewCard } from "../../../ui/components/ListView/components/ListViewCard";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { useAuth } from "../../lib/auth/hooks";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useI18n } from "../../lib/i18n";
import { authenticatedProApiFetch } from "../../lib/api/pro-api.client";
import type { DataCatalog, DataCatalogKey, EnumGroup, DataCatalogAlert } from "../../lib/api/products";
import {
  fetchEnumGroups,
  createDataCatalogAlert,
  updateDataCatalogAlert,
  deleteDataCatalogAlert,
  bulkDeleteDataCatalogAlerts,
  deleteDataCatalogKey,
  bulkDeleteDataCatalogKeys,
} from "../../lib/api/products";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { TabSwitcher } from "../../../ui/components/TabSwitcher";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import type { ContextAction } from "../../../ui/components/ContextActions";
import * as pageStyles from "../organizations/OrganizationDetailPage.css";
import * as s from "../products/ProductPage.css";
import {
  catalogSelect,
  catalogSelectLabel,
  catalogMenuDropdown,
  catalogMenuItem,
  catalogMenuItemDanger,
  catalogMenuDivider,
} from "../products/ProductPage.css";

const VALUE_TYPES = ["number", "enum", "bitmask", "string", "boolean", "alert"] as const;
const ACCESS_LEVELS = ["any", "owner", "technician", "admin"] as const;

const SEVERITY_LABELS: Record<number, string> = {
  1: "Suggestion",
  2: "Low",
  3: "Medium",
  4: "High",
  5: "Critical",
};

interface CatalogDetail extends DataCatalog {
  keys: DataCatalogKey[];
  alerts: DataCatalogAlert[];
  enumValues: Array<{
    id: string;
    catalogKeyId: string;
    key: string;
    label: string;
    description?: string | null;
    meta?: Record<string, unknown> | null;
  }>;
}

interface KeyFormValues {
  key: string;
  label: string;
  description: string;
  kind: "realtime" | "settings";
  valueType: string;
  rawUnit: string;
  canonicalUnit: string;
  precision: string;
  sortOrder: string;
  enumGroupId: string;
  section: string;
  isEditable: boolean;
  accessLevel: string;
  defaultValue: string;
  shadowName: string;
}

interface AlertFormValues {
  code: string;
  name: string;
  type: "alarm" | "fault";
  severity: string;
  description: string;
  sortOrder: string;
}

const emptyKeyForm = (kind: "realtime" | "settings" = "realtime"): KeyFormValues => ({
  key: "",
  label: "",
  description: "",
  kind,
  valueType: "number",
  rawUnit: "",
  canonicalUnit: "",
  precision: "",
  sortOrder: "0",
  enumGroupId: "",
  section: "",
  isEditable: true,
  accessLevel: "any",
  defaultValue: "",
  shadowName: "",
});

const emptyAlertForm = (): AlertFormValues => ({
  code: "",
  name: "",
  type: "alarm",
  severity: "3",
  description: "",
  sortOrder: "0",
});

function keyToForm(k: DataCatalogKey): KeyFormValues {
  return {
    key: k.key,
    label: k.label,
    description: k.description ?? "",
    kind: k.kind ?? "realtime",
    valueType: k.valueType,
    rawUnit: k.rawUnit ?? "",
    canonicalUnit: k.canonicalUnit ?? "",
    precision: k.precision != null ? String(k.precision) : "",
    sortOrder: String(k.sortOrder),
    enumGroupId: k.enumGroupId ?? "",
    section: k.section ?? "",
    isEditable: k.isEditable ?? true,
    accessLevel: k.accessLevel ?? "any",
    defaultValue: k.defaultValue ?? "",
    shadowName: k.shadowName ?? "",
  };
}

function alertToForm(a: DataCatalogAlert): AlertFormValues {
  return {
    code: a.code,
    name: a.name,
    type: a.type === "fault" ? "fault" : "alarm",
    severity: String(a.severity),
    description: a.description ?? "",
    sortOrder: String(a.sortOrder),
  };
}

export function DataCatalogDetailPage({ catalogId }: { catalogId: string }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");

  const [catalog, setCatalog] = React.useState<CatalogDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [enumGroups, setEnumGroups] = React.useState<EnumGroup[]>([]);
  const { confirm, ConfirmModal } = useConfirmModal();

  const [activeTab, setActiveTab] = React.useState<"realtime" | "settings" | "alerts">("realtime");
  const [activeShadow, setActiveShadow] = React.useState<string | null>(null);

  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse(`data-catalog-${catalogId}`);

  // Key modal
  const [keyModal, setKeyModal] = React.useState<{
    open: boolean;
    editing?: DataCatalogKey;
    bakedKind?: "realtime" | "settings";
    shadowName?: string;
  }>({ open: false });
  const [keyForm, setKeyForm] = React.useState<KeyFormValues>(emptyKeyForm());
  const [keyError, setKeyError] = React.useState<string | null>(null);
  const [keySaving, setKeySaving] = React.useState(false);

  // Alert modal
  const [alertModal, setAlertModal] = React.useState<{
    open: boolean;
    editing?: DataCatalogAlert;
  }>({ open: false });
  const [alertForm, setAlertForm] = React.useState<AlertFormValues>(emptyAlertForm());
  const [alertError, setAlertError] = React.useState<string | null>(null);
  const [alertSaving, setAlertSaving] = React.useState(false);

  const load = React.useCallback(() => {
    if (!token) return;
    setLoading(true);
    authenticatedProApiFetch<CatalogDetail>(`/data-catalogs/${catalogId}`, token)
      .then((data) => {
        setCatalog({
          ...data,
          alerts: data.alerts ?? [],
        });
      })
      .catch((err) =>
        setLoadError(err?.message || t("pro.system.dataCatalog.error.loadDetail")),
      )
      .finally(() => setLoading(false));
    if (token) {
      fetchEnumGroups(token)
        .then(setEnumGroups)
        .catch(() => {});
    }
  }, [token, catalogId, t]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Set default active shadow when catalog loads
  React.useEffect(() => {
    if (!catalog) return;
    const shadowNames = Array.from(
      new Set(
        catalog.keys
          .filter((k) => k.kind === "settings" && k.shadowName)
          .map((k) => k.shadowName as string),
      ),
    ).sort((a, b) => {
      if (a === "settings") return -1;
      if (b === "settings") return 1;
      return a.localeCompare(b);
    });
    if (shadowNames.length > 0 && !activeShadow) {
      setActiveShadow(shadowNames[0]);
    }
  }, [catalog, activeShadow]);

  const handleDeactivate = async () => {
    if (!token || !catalog) return;
    const ok = await confirm(
      t("pro.system.dataCatalog.confirm.deactivateTitle", { name: catalog.name }),
      t("pro.system.dataCatalog.confirm.deactivateBody"),
      t("pro.system.dataCatalog.confirm.deactivate"),
      "danger",
    );
    if (!ok) return;
    await authenticatedProApiFetch(`/data-catalogs/${catalogId}`, token, {
      method: "PATCH",
      body: JSON.stringify({ isActive: false }),
    });
    load();
  };

  const handleActivate = async () => {
    if (!token || !catalog) return;
    const ok = await confirm(
      t("pro.system.dataCatalog.confirm.activateTitle", { name: catalog.name }),
      t("pro.system.dataCatalog.confirm.activateBody"),
      t("pro.system.dataCatalog.confirm.activate"),
      "primary",
    );
    if (!ok) return;
    await authenticatedProApiFetch(`/data-catalogs/${catalogId}`, token, {
      method: "PATCH",
      body: JSON.stringify({ isActive: true }),
    });
    load();
  };

  const handleDelete = async () => {
    if (!token || !catalog) return;
    const ok = await confirm(
      t("pro.system.dataCatalog.confirm.deleteTitle", { name: catalog.name }),
      t("pro.system.dataCatalog.confirm.deleteBody"),
      t("shared.common.delete"),
      "danger",
    );
    if (!ok) return;
    await authenticatedProApiFetch(`/data-catalogs/${catalogId}`, token, {
      method: "DELETE",
    });
    navigate({ to: "/system/data-catalog" });
  };

  // ── Key actions ───────────────────────────────────────────────────────────
  const openAddRealtimeKey = () => {
    setKeyForm(emptyKeyForm("realtime"));
    setKeyError(null);
    setKeyModal({ open: true, bakedKind: "realtime" });
  };

  const openAddSettingsKey = (shadowName: string) => {
    setKeyForm({ ...emptyKeyForm("settings"), shadowName });
    setKeyError(null);
    setKeyModal({ open: true, bakedKind: "settings", shadowName });
  };

  const openEditKey = React.useCallback((k: DataCatalogKey) => {
    setKeyForm(keyToForm(k));
    setKeyError(null);
    setKeyModal({ open: true, editing: k, bakedKind: k.kind ?? "realtime" });
  }, []);

  const handleSaveKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !keyForm.key.trim() || !keyForm.label.trim()) return;
    setKeySaving(true);
    setKeyError(null);
    const isSettings = keyForm.kind === "settings";
    const body: Record<string, unknown> = {
      key: keyForm.key.trim(),
      label: keyForm.label.trim(),
      description: keyForm.description.trim() || null,
      kind: keyForm.kind,
      valueType: keyForm.valueType,
      rawUnit: keyForm.rawUnit.trim() || null,
      canonicalUnit: keyForm.canonicalUnit.trim() || null,
      precision: keyForm.precision !== "" ? parseInt(keyForm.precision, 10) : null,
      sortOrder: parseInt(keyForm.sortOrder, 10) || 0,
      enumGroupId: keyForm.enumGroupId || null,
      section: isSettings ? keyForm.section.trim() || null : null,
      isEditable: isSettings ? keyForm.isEditable : true,
      accessLevel: isSettings ? keyForm.accessLevel || "any" : "any",
      defaultValue: isSettings ? keyForm.defaultValue.trim() || null : null,
      shadowName: isSettings ? keyForm.shadowName.trim() || null : null,
    };
    try {
      if (keyModal.editing) {
        await authenticatedProApiFetch(
          `/data-catalogs/${catalogId}/keys/${keyModal.editing.id}`,
          token,
          { method: "PATCH", body: JSON.stringify(body) },
        );
      } else {
        await authenticatedProApiFetch(`/data-catalogs/${catalogId}/keys`, token, {
          method: "POST",
          body: JSON.stringify(body),
        });
      }
      setKeyModal({ open: false });
      load();
    } catch (err: any) {
      setKeyError(err?.message || t("pro.system.dataCatalog.error.saveKey"));
    } finally {
      setKeySaving(false);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (!token) return;
    const ok = await confirm(
      "Delete key?",
      "This key will be permanently deleted. This cannot be undone.",
      t("shared.common.delete"),
      "danger",
    );
    if (!ok) return;
    try {
      await deleteDataCatalogKey(token, catalogId, keyId);
      load();
    } catch (err: any) {
      setKeyError(err?.message || "Failed to delete key");
    }
  };

  const handleBulkDeleteKeys = React.useCallback(async (ids: string[]) => {
    if (!token || ids.length === 0) return;
    const ok = await confirm(
      "Delete selected keys?",
      `Delete ${ids.length} key${ids.length !== 1 ? "s" : ""}? This cannot be undone.`,
      t("shared.common.delete"),
      "danger",
    );
    if (!ok) return;
    try {
      await bulkDeleteDataCatalogKeys(token, catalogId, ids);
      load();
    } catch (err: any) {
      setKeyError(err?.message || "Failed to delete keys");
    }
  }, [token, catalogId, confirm, t, load]);

  // ── Alert actions ─────────────────────────────────────────────────────────
  const openAddAlert = () => {
    setAlertForm(emptyAlertForm());
    setAlertError(null);
    setAlertModal({ open: true });
  };

  const openEditAlert = React.useCallback((a: DataCatalogAlert) => {
    setAlertForm(alertToForm(a));
    setAlertError(null);
    setAlertModal({ open: true, editing: a });
  }, []);

  const handleSaveAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !alertForm.code.trim() || !alertForm.name.trim()) return;
    setAlertSaving(true);
    setAlertError(null);
    const body = {
      code: alertForm.code.trim(),
      name: alertForm.name.trim(),
      type: alertForm.type,
      severity: parseInt(alertForm.severity, 10) || 3,
      description: alertForm.description.trim() || null,
      sortOrder: parseInt(alertForm.sortOrder, 10) || 0,
    };
    try {
      if (alertModal.editing) {
        await updateDataCatalogAlert(token, catalogId, alertModal.editing.id, body);
      } else {
        await createDataCatalogAlert(token, catalogId, body);
      }
      setAlertModal({ open: false });
      load();
    } catch (err: any) {
      setAlertError(err?.message || "Failed to save alert");
    } finally {
      setAlertSaving(false);
    }
  };

  const handleDeleteAlert = async (alertId: string) => {
    if (!token) return;
    const ok = await confirm(
      "Delete alert?",
      "This alert definition will be permanently deleted. This cannot be undone.",
      t("shared.common.delete"),
      "danger",
    );
    if (!ok) return;
    try {
      await deleteDataCatalogAlert(token, catalogId, alertId);
      load();
    } catch (err: any) {
      setAlertError(err?.message || "Failed to delete alert");
    }
  };

  const handleBulkDeleteAlerts = React.useCallback(async (ids: string[]) => {
    if (!token || ids.length === 0) return;
    const ok = await confirm(
      "Delete selected alerts?",
      `Delete ${ids.length} alert${ids.length !== 1 ? "s" : ""}? This cannot be undone.`,
      t("shared.common.delete"),
      "danger",
    );
    if (!ok) return;
    try {
      await bulkDeleteDataCatalogAlerts(token, catalogId, ids);
      load();
    } catch (err: any) {
      setAlertError(err?.message || "Failed to delete alerts");
    }
  }, [token, catalogId, confirm, t, load]);

  // ── ListView data ─────────────────────────────────────────────────────────
  const makeKeysFetchList = React.useCallback(
    (keys: DataCatalogKey[]) =>
      (params: ListParams): Promise<ListResult<DataCatalogKey>> => {
        const search = params.search?.toLowerCase();
        const all = [...keys].sort((a, b) => a.sortOrder - b.sortOrder);
        const filtered = search
          ? all.filter(
              (k) =>
                k.key.toLowerCase().includes(search) ||
                k.label.toLowerCase().includes(search) ||
                k.section?.toLowerCase().includes(search),
            )
          : all;
        const start = (params.page - 1) * params.limit;
        return Promise.resolve({
          data: filtered.slice(start, start + params.limit),
          total: filtered.length,
        });
      },
    [],
  );

  const realtimeKeys = React.useMemo(
    () => (catalog?.keys ?? []).filter((k) => (k.kind ?? "realtime") === "realtime"),
    [catalog?.keys],
  );

  const allSettingsKeys = React.useMemo(
    () => (catalog?.keys ?? []).filter((k) => k.kind === "settings"),
    [catalog?.keys],
  );

  const shadowNames = React.useMemo(
    () =>
      Array.from(
        new Set(
          allSettingsKeys
            .filter((k) => k.shadowName)
            .map((k) => k.shadowName as string),
        ),
      ).sort((a, b) => {
        if (a === "settings") return -1;
        if (b === "settings") return 1;
        return a.localeCompare(b);
      }),
    [allSettingsKeys],
  );

  const settingsKeysByShadow = React.useMemo(() => {
    const map = new Map<string, DataCatalogKey[]>();
    for (const name of shadowNames) {
      map.set(
        name,
        allSettingsKeys.filter((k) => k.shadowName === name),
      );
    }
    return map;
  }, [shadowNames, allSettingsKeys]);

  const getKeyContextActions = React.useCallback(
    (row: DataCatalogKey): ListViewContextAction[] => {
      return [
        {
          id: "edit",
          label: t("shared.common.edit"),
          icon: <Pencil size={14} />,
          bulk: false,
          onClick: () => openEditKey(row),
        },
        {
          id: "delete",
          label: t("shared.common.delete"),
          icon: <Trash2 size={14} />,
          danger: true,
          onClick: (ids) => {
            void handleBulkDeleteKeys(ids.length ? ids : [row.id]);
          },
        },
      ];
    },
    [openEditKey, t, handleBulkDeleteKeys],
  );

  const renderKeyCard = React.useCallback((row: DataCatalogKey) => {
    const unit = row.rawUnit
      ? row.canonicalUnit && row.canonicalUnit !== row.rawUnit
        ? `${row.rawUnit} → ${row.canonicalUnit}`
        : row.rawUnit
      : row.canonicalUnit;
    const badges = [
      { id: "key", label: row.key },
      { id: "type", label: row.valueType },
      ...(unit ? [{ id: "unit", label: unit }] : []),
    ];
    return (
      <ListViewCard
        id={row.id}
        layout="stack"
        imageFormat="none"
        compact
        title={row.label}
        badges={badges}
        onClick={() => openEditKey(row)}
      />
    );
  }, [openEditKey]);

  const alerts = catalog?.alerts ?? [];
  const makeAlertsFetchList = React.useCallback(
    (params: ListParams): Promise<ListResult<DataCatalogAlert>> => {
      const search = params.search?.toLowerCase();
      const all = [...alerts]
        .filter((a) => a.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const filtered = search
        ? all.filter(
            (a) =>
              a.code.toLowerCase().includes(search) ||
              a.name.toLowerCase().includes(search) ||
              a.description?.toLowerCase().includes(search),
          )
        : all;
      const start = (params.page - 1) * params.limit;
      return Promise.resolve({
        data: filtered.slice(start, start + params.limit),
        total: filtered.length,
      });
    },
    [alerts],
  );

  const getAlertContextActions = React.useCallback(
    (row: DataCatalogAlert): ListViewContextAction[] => {
      return [
        {
          id: "edit",
          label: t("shared.common.edit"),
          icon: <Pencil size={14} />,
          bulk: false,
          onClick: () => openEditAlert(row),
        },
        {
          id: "delete",
          label: t("shared.common.delete"),
          icon: <Trash2 size={14} />,
          danger: true,
          onClick: (ids) => {
            void handleBulkDeleteAlerts(ids.length ? ids : [row.id]);
          },
        },
      ];
    },
    [openEditAlert, t, handleBulkDeleteAlerts],
  );

  const renderAlertCard = React.useCallback((row: DataCatalogAlert) => {
    const badges = [
      { id: "code", label: row.code },
      { id: "type", label: row.type },
      { id: "severity", label: SEVERITY_LABELS[row.severity] ?? String(row.severity) },
    ];
    return (
      <ListViewCard
        id={row.id}
        layout="stack"
        imageFormat="none"
        compact
        title={row.name}
        subtitle={row.description ?? undefined}
        badges={badges}
        onClick={() => openEditAlert(row)}
      />
    );
  }, [openEditAlert]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading)
    return (
      <div className={pageStyles.page}>
        <div style={{ maxWidth: 960, width: "100%" }}>
          <Text level="body-medium" style={{ color: "#9CA3AF" }}>
            {t("shared.common.loading")}
          </Text>
        </div>
      </div>
    );

  if (loadError || !catalog)
    return (
      <div className={pageStyles.page}>
        <div style={{ maxWidth: 960, width: "100%" }}>
          <Callout variant="error">
            {loadError ?? t("pro.system.dataCatalog.error.notFound")}
          </Callout>
        </div>
      </div>
    );

  const contextActions: ContextAction[] = [
    {
      id: "delete",
      label: t("shared.common.delete"),
      icon: <Trash2 size={16} />,
      color: "neutral",
      onClick: handleDelete,
    },
  ];

  if (catalog.isActive) {
    contextActions.unshift({
      id: "deactivate",
      label: t("pro.system.dataCatalog.confirm.deactivate"),
      icon: <Trash2 size={16} />,
      color: "neutral",
      onClick: handleDeactivate,
    });
  } else {
    contextActions.unshift({
      id: "activate",
      label: t("pro.system.dataCatalog.confirm.activate"),
      icon: <Plus size={16} />,
      color: "neutral",
      onClick: handleActivate,
    });
  }

  const headerBadges: React.ReactNode[] = [];
  if (!catalog.isActive) {
    headerBadges.push(
      <span
        key="inactive"
        style={{
          fontSize: 11,
          fontWeight: 600,
          padding: "2px 8px",
          borderRadius: 9999,
          border: "1px solid currentColor",
          opacity: 0.4,
        }}
      >
        {t("pro.system.dataCatalog.badge.inactive")}
      </span>,
    );
  }

  const sectionLayout = bindSectionLayout(
    activeTab === "realtime"
      ? [
          {
            id: "realtime-definitions",
            label: "Real-time definitions",
            icon: <Database size={14} />,
          },
        ]
      : activeTab === "alerts"
        ? [
            {
              id: "alert-definitions",
              label: "Alert definitions",
              icon: <Database size={14} />,
            },
          ]
        : shadowNames.length === 0
          ? [
              {
                id: "settings-empty",
                label: "Settings definitions",
                icon: <Database size={14} />,
              },
            ]
          : shadowNames.map((shadowName) => ({
              id: `settings-${shadowName}`,
              label: shadowName,
              icon: <Database size={14} />,
            })),
  );

  return (
    <>
      <DetailPageLayout
        backLink={{
          label: t("pro.system.dataCatalog.title"),
          onClick: () => navigate({ to: "/system/data-catalog" }),
        }}
        header={{
          media: <Database size={28} color="#9CA3AF" />,
          title: catalog.name,
          badges: headerBadges,
        }}
        contextActions={contextActions}
        {...sectionLayout}
        tabs={
          <TabSwitcher
            tabs={[
              { value: "realtime", label: "Real-time" },
              { value: "settings", label: "Settings" },
              { value: "alerts", label: "Alerts" },
            ]}
            value={activeTab}
            onChange={(v) => setActiveTab(v as "realtime" | "settings" | "alerts")}
            aria-label={t("pro.system.dataCatalog.aria.tabs")}
          />
        }
      >
        {/* Real-time tab */}
        {activeTab === "realtime" && (
          <CollapsibleSection
            id="realtime-definitions"
            title={t("pro.system.dataCatalog.realtime.definitionsTitle")}
            icon={<Database size={20} />}
            expanded={isExpanded("realtime-definitions")}
            onToggleExpanded={() => toggle("realtime-definitions")}
            contextActions={[
              {
                id: "add",
                label: "Add Real-time Key",
                icon: <Plus size={14} />,
                onClick: openAddRealtimeKey,
              },
            ]}
          >
            <ListView<DataCatalogKey>
              key={`realtime-${realtimeKeys.length}`}
              listId={`catalog-realtime-${catalogId}`}
              columns={[]}
              fetchList={makeKeysFetchList(realtimeKeys)}
              getContextActions={getKeyContextActions}
              renderCard={renderKeyCard}
              compact
              enabledViews={["stack"]}
              defaultPageSize={10}
              disableLocalPersistence
              searchPlaceholder="Search keys..."
              emptyTitle="No real-time keys"
              emptySubtitle={t("pro.system.dataCatalog.realtime.emptySubtitleStart")}
              enableSelection
            />
          </CollapsibleSection>
        )}

        {/* Settings tab */}
        {activeTab === "settings" && (
          <>
            {shadowNames.length === 0 ? (
              <CollapsibleSection
                id="settings-empty"
                title={t("pro.system.dataCatalog.settings.definitionsTitle")}
                icon={<Database size={20} />}
                expanded={isExpanded("settings-empty")}
                onToggleExpanded={() => toggle("settings-empty")}
                contextActions={[
                  {
                    id: "add",
                    label: "Add Settings Key",
                    icon: <Plus size={14} />,
                    onClick: () => openAddSettingsKey("settings"),
                  },
                ]}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12,
                    padding: "36px 0",
                  }}
                >
                  <Text level="body-small" style={{ color: "#9CA3AF" }}>
                    No settings keys defined yet.
                  </Text>
                  <Button
                    type="button"
                    color="primary"
                    variant="outline"
                    iconLeft={<Plus size={14} />}
                    onClick={() => openAddSettingsKey("settings")}
                  >
                    Add Settings Key
                  </Button>
                </div>
              </CollapsibleSection>
            ) : (
              shadowNames.map((shadowName) => {
                const keys = settingsKeysByShadow.get(shadowName) ?? [];
                return (
                  <CollapsibleSection
                    key={shadowName}
                    id={`settings-${shadowName}`}
                    title={shadowName}
                    subtitle={`${keys.length} key${keys.length !== 1 ? "s" : ""}`}
                    icon={<Database size={20} />}
                    expanded={isExpanded(`settings-${shadowName}`)}
                    onToggleExpanded={() => toggle(`settings-${shadowName}`)}
                    contextActions={[
                      {
                        id: "add",
                        label: "Add Key",
                        icon: <Plus size={14} />,
                        onClick: () => openAddSettingsKey(shadowName),
                      },
                    ]}
                  >
                    <ListView<DataCatalogKey>
                      key={`settings-${shadowName}-${keys.length}`}
                      listId={`catalog-settings-${catalogId}-${shadowName}`}
                      columns={[]}
                      fetchList={makeKeysFetchList(keys)}
                      getContextActions={getKeyContextActions}
                      renderCard={renderKeyCard}
                      compact
                      enabledViews={["stack"]}
                      defaultPageSize={10}
                      disableLocalPersistence
                      searchPlaceholder="Search keys..."
                      emptyTitle={`No settings keys for ${shadowName}`}
                      emptySubtitle={t("pro.system.dataCatalog.settings.emptySubtitleStart")}
                      enableSelection
                    />
                  </CollapsibleSection>
                );
              })
            )}
          </>
        )}

        {/* Alerts tab */}
        {activeTab === "alerts" && (
          <CollapsibleSection
            id="alert-definitions"
            title={t("pro.system.dataCatalog.alerts.definitionsTitle")}
            icon={<Database size={20} />}
            expanded={isExpanded("alert-definitions")}
            onToggleExpanded={() => toggle("alert-definitions")}
            contextActions={[
              {
                id: "add",
                label: "Add Alert",
                icon: <Plus size={14} />,
                onClick: openAddAlert,
              },
            ]}
          >
            <ListView<DataCatalogAlert>
              key={`alerts-${alerts.length}`}
              listId={`catalog-alerts-${catalogId}`}
              columns={[]}
              fetchList={makeAlertsFetchList}
              getContextActions={getAlertContextActions}
              renderCard={renderAlertCard}
              compact
              enabledViews={["stack"]}
              defaultPageSize={10}
              disableLocalPersistence
              searchPlaceholder="Search alerts..."
              emptyTitle="No alerts defined"
              emptySubtitle={t("pro.system.dataCatalog.alerts.emptySubtitle")}
              enableSelection
            />
          </CollapsibleSection>
        )}
      </DetailPageLayout>

      <ConfirmModal />

      {/* Key modal */}
      <Modal
        open={keyModal.open}
        setOpen={(v) => !v && setKeyModal({ open: false })}
        minWidth="460px"
        maxWidth="580px"
        closeOnClickOff
      >
        <div style={{ padding: "24px 28px" }}>
          <Text level="h2" weight="bold" style={{ marginBottom: 20 }}>
            {keyModal.editing
              ? keyModal.bakedKind === "settings"
                ? "Edit Settings Key"
                : "Edit Real-time Key"
              : keyModal.bakedKind === "settings"
              ? "Add Settings Key"
              : "Add Real-time Key"}
          </Text>
          <form onSubmit={handleSaveKey}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", gap: 12 }}>
                <TextInput
                  label={t("pro.system.dataCatalog.keys.col.key")}
                  value={keyForm.key}
                  onChange={(e) => setKeyForm((p) => ({ ...p, key: e.target.value }))}
                  placeholder="raw_key"
                  disabled={keySaving || !!keyModal.editing}
                />
                <TextInput
                  label={t("pro.system.dataCatalog.keys.col.label")}
                  value={keyForm.label}
                  onChange={(e) => setKeyForm((p) => ({ ...p, label: e.target.value }))}
                  placeholder={t("pro.system.dataCatalog.form.displayLabelPlaceholder")}
                  disabled={keySaving}
                />
              </div>
              <TextInput
                label={t("pro.system.dataCatalog.form.description")}
                value={keyForm.description}
                onChange={(e) =>
                  setKeyForm((p) => ({ ...p, description: e.target.value }))
                }
                placeholder={t("pro.system.dataCatalog.form.descriptionPlaceholder")}
                disabled={keySaving}
              />

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label className={catalogSelectLabel}>{t("pro.system.dataCatalog.form.valueType")}</label>
                <select
                  value={keyForm.valueType}
                  onChange={(e) => {
                    const val = e.target.value;
                    setKeyForm((p) => ({
                      ...p,
                      valueType: val,
                      enumGroupId:
                        val !== "enum" && val !== "bitmask" ? "" : p.enumGroupId,
                    }));
                  }}
                  disabled={keySaving}
                  className={catalogSelect}
                >
                  {VALUE_TYPES.map((vt) => (
                    <option key={vt} value={vt}>
                      {vt === "alert" ? "Alert codes" : vt}
                    </option>
                  ))}
                </select>
              </div>

              {(keyForm.valueType === "enum" || keyForm.valueType === "bitmask") && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label className={catalogSelectLabel}>{t("pro.system.dataCatalog.form.enumGroup")}</label>
                  <select
                    value={keyForm.enumGroupId}
                    onChange={(e) =>
                      setKeyForm((p) => ({ ...p, enumGroupId: e.target.value }))
                    }
                    disabled={keySaving}
                    className={catalogSelect}
                  >
                    <option value="">{t("pro.system.dataCatalog.form.perKeyValues")}</option>
                    {enumGroups
                      .filter((g) => g.isActive)
                      .map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div style={{ display: "flex", gap: 12 }}>
                <TextInput
                  label={t("pro.system.dataCatalog.form.rawUnit")}
                  value={keyForm.rawUnit}
                  onChange={(e) =>
                    setKeyForm((p) => ({ ...p, rawUnit: e.target.value }))
                  }
                  placeholder="W"
                  disabled={keySaving}
                />
                <TextInput
                  label={t("pro.system.dataCatalog.form.canonicalUnit")}
                  value={keyForm.canonicalUnit}
                  onChange={(e) =>
                    setKeyForm((p) => ({ ...p, canonicalUnit: e.target.value }))
                  }
                  placeholder="kW"
                  disabled={keySaving}
                />
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <TextInput
                  label={t("pro.system.dataCatalog.form.precision")}
                  value={keyForm.precision}
                  onChange={(e) =>
                    setKeyForm((p) => ({ ...p, precision: e.target.value }))
                  }
                  placeholder="2"
                  disabled={keySaving}
                />
                <TextInput
                  label={t("pro.system.dataCatalog.form.sortOrder")}
                  value={keyForm.sortOrder}
                  onChange={(e) =>
                    setKeyForm((p) => ({ ...p, sortOrder: e.target.value }))
                  }
                  placeholder="0"
                  disabled={keySaving}
                />
              </div>

              {keyForm.kind === "settings" && (
                <div
                  style={{
                    borderTop: "1px solid #F3F4F6",
                    paddingTop: 12,
                    marginTop: 4,
                  }}
                >
                  <Text
                    level="body-small"
                    weight="bold"
                    style={{ marginBottom: 10, opacity: 0.6 }}
                  >
                    Settings Options
                  </Text>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <label className={catalogSelectLabel}>{t("pro.system.dataCatalog.form.shadowName")}</label>
                      <select
                        value={keyForm.shadowName}
                        onChange={(e) =>
                          setKeyForm((p) => ({ ...p, shadowName: e.target.value }))
                        }
                        disabled={keySaving}
                        className={catalogSelect}
                      >
                        <option value="">{t("pro.system.dataCatalog.form.none")}</option>
                        {shadowNames.map((sn) => (
                          <option key={sn} value={sn}>
                            {sn}
                          </option>
                        ))}
                        {keyForm.shadowName &&
                          !shadowNames.includes(keyForm.shadowName) && (
                            <option value={keyForm.shadowName}>
                              {keyForm.shadowName} (new)
                            </option>
                          )}
                      </select>
                      <TextInput
                        label=""
                        value={keyForm.shadowName}
                        onChange={(e) =>
                          setKeyForm((p) => ({ ...p, shadowName: e.target.value }))
                        }
                        placeholder={t("pro.system.dataCatalog.form.newShadowPlaceholder")}
                        disabled={keySaving}
                      />
                    </div>

                    <div style={{ display: "flex", gap: 12 }}>
                      <TextInput
                        label={t("pro.system.dataCatalog.form.section")}
                        value={keyForm.section}
                        onChange={(e) =>
                          setKeyForm((p) => ({ ...p, section: e.target.value }))
                        }
                        placeholder={t("pro.system.dataCatalog.form.sectionPlaceholderGeneral")}
                        disabled={keySaving}
                      />
                      <TextInput
                        label={t("pro.system.dataCatalog.form.defaultValue")}
                        value={keyForm.defaultValue}
                        onChange={(e) =>
                          setKeyForm((p) => ({ ...p, defaultValue: e.target.value }))
                        }
                        placeholder="0"
                        disabled={keySaving}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          flex: 1,
                        }}
                      >
                        <label className={catalogSelectLabel}>{t("pro.system.dataCatalog.form.accessLevel")}</label>
                        <select
                          value={keyForm.accessLevel}
                          onChange={(e) =>
                            setKeyForm((p) => ({ ...p, accessLevel: e.target.value }))
                          }
                          disabled={keySaving}
                          className={catalogSelect}
                        >
                          {ACCESS_LEVELS.map((al) => (
                            <option key={al} value={al}>
                              {al}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          paddingBottom: 4,
                        }}
                      >
                        <input
                          type="checkbox"
                          id="key-is-editable"
                          checked={keyForm.isEditable}
                          onChange={(e) =>
                            setKeyForm((p) => ({ ...p, isEditable: e.target.checked }))
                          }
                          disabled={keySaving}
                        />
                        <label
                          htmlFor="key-is-editable"
                          className={catalogSelectLabel}
                          style={{ marginBottom: 0, cursor: "pointer" }}
                        >
                          Editable
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {keyError && (
              <div style={{ marginTop: 12 }}>
                <Callout variant="error">{keyError}</Callout>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 20,
              }}
            >
              <Button
                type="button"
                color="neutral"
                onClick={() => setKeyModal({ open: false })}
                disabled={keySaving}
              >
                {t("shared.common.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                iconLeft={<Save size={14} />}
                disabled={keySaving || !keyForm.key.trim() || !keyForm.label.trim()}
              >
                {keySaving ? "Saving..." : "Save Key"}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Alert modal */}
      <Modal
        open={alertModal.open}
        setOpen={(v) => !v && setAlertModal({ open: false })}
        minWidth="460px"
        maxWidth="580px"
        closeOnClickOff
      >
        <div style={{ padding: "24px 28px" }}>
          <Text level="h2" weight="bold" style={{ marginBottom: 20 }}>
            {alertModal.editing ? "Edit Alert" : "Add Alert"}
          </Text>
          <form onSubmit={handleSaveAlert}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", gap: 12 }}>
                <TextInput
                  label={t("pro.system.dataCatalog.alerts.form.code")}
                  value={alertForm.code}
                  onChange={(e) =>
                    setAlertForm((p) => ({ ...p, code: e.target.value }))
                  }
                  placeholder={t("pro.system.dataCatalog.alerts.form.codePlaceholder")}
                  disabled={alertSaving || !!alertModal.editing}
                />
                <TextInput
                  label={t("pro.system.dataCatalog.col.name")}
                  value={alertForm.name}
                  onChange={(e) =>
                    setAlertForm((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder={t("pro.system.dataCatalog.alerts.form.namePlaceholder")}
                  disabled={alertSaving}
                />
              </div>
              <TextInput
                label={t("pro.system.dataCatalog.form.description")}
                value={alertForm.description}
                onChange={(e) =>
                  setAlertForm((p) => ({ ...p, description: e.target.value }))
                }
                placeholder={t("pro.system.dataCatalog.form.descriptionPlaceholder")}
                disabled={alertSaving}
              />
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                  <label className={catalogSelectLabel}>{t("pro.system.dataCatalog.keys.col.type")}</label>
                  <select
                    value={alertForm.type}
                    onChange={(e) =>
                      setAlertForm((p) => ({
                        ...p,
                        type: e.target.value as "alarm" | "fault",
                      }))
                    }
                    disabled={alertSaving}
                    className={catalogSelect}
                  >
                    <option value="alarm">{t("pro.system.dataCatalog.alerts.type.alarm")}</option>
                    <option value="fault">{t("pro.system.dataCatalog.alerts.type.fault")}</option>
                  </select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                  <label className={catalogSelectLabel}>{t("pro.system.enums.col.severity")}</label>
                  <select
                    value={alertForm.severity}
                    onChange={(e) =>
                      setAlertForm((p) => ({ ...p, severity: e.target.value }))
                    }
                    disabled={alertSaving}
                    className={catalogSelect}
                  >
                    {Object.entries(SEVERITY_LABELS).map(([val, label]) => (
                      <option key={val} value={val}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <TextInput
                label={t("pro.system.dataCatalog.form.sortOrder")}
                value={alertForm.sortOrder}
                onChange={(e) =>
                  setAlertForm((p) => ({ ...p, sortOrder: e.target.value }))
                }
                placeholder="0"
                disabled={alertSaving}
              />
            </div>

            {alertError && (
              <div style={{ marginTop: 12 }}>
                <Callout variant="error">{alertError}</Callout>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 20,
              }}
            >
              <Button
                type="button"
                color="neutral"
                onClick={() => setAlertModal({ open: false })}
                disabled={alertSaving}
              >
                {t("shared.common.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                iconLeft={<Save size={14} />}
                disabled={
                  alertSaving || !alertForm.code.trim() || !alertForm.name.trim()
                }
              >
                {alertSaving ? "Saving..." : "Save Alert"}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
